import React, { useEffect } from "react";
import { useParams, useHistory, Redirect, withRouter } from "react-router-dom";
import { inject } from "mobx-react";
import { Helmet } from "react-helmet-async";
import VisibilitySensor from "react-visibility-sensor";
import Container from "reactstrap/lib/Container";
import { useQuery } from "@apollo/client";
import { STREET_ONE_QUERY, HOUSES_QUERY_V3 } from "Services/queries";
import useDefaultParams from "Services/useDefaultParams";
import { generateUrl } from "Services/withDefaultParams";
import { sendMetriks } from "Services/sendMetriks";
import { generateStreetPart, generateValidChankUrl } from "Helpers/UrlManager";
import { generateStreetFullName } from "Helpers/localeText";
import { isMainSiteCode } from "Utils/siteConfig";
import Routes from "Configs/routes";
import ReviewsSend101RublesHOC from "HOC/ReviewsSend101RublesHOC";
import SeoBlockHOC from "HOC/SeoBlockHOC";
import RandomMobileTariffListHOC from "HOC/RandomMobileTariffListHOC";
import SeoAddressLayout from "Layouts/SeoAddressLayout";
import BenefitsLayout from "Layouts/BenefitsLayout";
import HideBeforeMount from "Layouts/HideBeforeMount";
import { BigBlockStub } from "Layouts/Stubs";
import { SeoBlockContainer } from "Containers/SeoBlock";
import { AddReviewContainerV2, ReviewListContainerV3 } from "Containers/Review";
import AddressSearchContainer from "Containers/AddressSearchContainer";
import YandexWidgetReviews from "Containers/YandexWidgetReviews";
import { Redirect404 } from "ComponentsUI/Redirects";
import ListOfHouses from "ComponentsUI/ListOfHouses";
import { BlockReviews } from "ComponentsUI/Blocks";
import { CheckSpeed } from "ComponentsUI/CheckSpeed";
import { Banner101Rubles } from "ComponentsUI/Banner";
import ProvidersContainer from "./ProvidersContainer";
import { PaddingBottom } from "ComponentsUI/Spacings";
import { ContainerPaddingBottom } from "ComponentsUI/Spacings";

function Street(props) {
	const {
		ApplicationModel: {
			text,
			siteCode,
			siteConfig,
			sendingStore,
			searcheModelOneString,
			searcheModel,
			commonViewModel,
			windowSizeModel: { windowSizes },
		},
		match: { params },
	} = props;

	const routerParams = useParams();
	const historyParams = useHistory();
	const defaultParams = useDefaultParams(routerParams);

	const one_string_search = siteConfig?.settings?.one_string_search?.enabled;

	const onClickHouseHandler = (house) => {
		commonViewModel.changeShowModalOnRates(true);
		sendMetriks("CLICK_ON_NUMBER_HOUSE");
		if (one_string_search) {
			searcheModelOneString.setHouseId(house.id);
		} else {
			searcheModel.setHouseId(house.id);
		}
	};

	const wasViewedHandler = (visible) => {
		sendMetriks("VIEW_TEST_SKOR");
	};

	const canonical = () => {
		const location = generateUrl(Routes.AddressStreet.route, {
			regionUrl: params.regionUrl,
			districtName: params.districtName,
			districtId: params.districtId,
			streetName: params.streetName,
			streetId: params.streetId,
		});

		return (
			<Helmet>
				<link
					rel="canonical"
					href={`${siteConfig.ssl === true ? `https` : "http"}://${
						siteConfig.domain_name_full
					}${location}`}
				/>
			</Helmet>
		);
	};

	useEffect(() => {
		searcheModelOneString.removeHouseFromInput();
	}, []);

	const streetQuery = useQuery(STREET_ONE_QUERY, {
		skip: !defaultParams.regionUrl,
		variables: {
			id: +routerParams.streetId,
		},
	});
	const housesQuery = useQuery(HOUSES_QUERY_V3, {
		skip: !defaultParams.regionUrl,
		variables: {
			filter: `street_id=${+routerParams.streetId}&with_tariffs=1&with_default=1`,
		},
	});

	let isStub = housesQuery.loading || streetQuery.loading;
	let houses = [];
	let streetsOne = {};

	useEffect(() => {
		if (Object.keys(streetsOne).length > 0) {
			if (one_string_search) {
				searcheModelOneString.setCurrentStreet({
					data: {
						street_id: streetsOne.id,
						street_name: streetsOne.name,
						street_name_full: `${streetsOne.name} ${streetsOne.type}`,
					},
					result: `${streetsOne.name} ${streetsOne.type}`,
				});
			} else {
				searcheModel.setCurrentStreet({
					...streetsOne,
					street_id: streetsOne.id,
				});
			}
		}
	}, [streetsOne]);

	let benefits = (
		<HideBeforeMount>
			<BenefitsLayout colorless={true} withoutTopMargin={true} />
		</HideBeforeMount>
	);

	let topComponent = <BigBlockStub />;

	if (!isStub) {
		houses = housesQuery.data.housesV3.data;
		streetsOne = streetQuery.data.streetsOne;
		let districtID = +routerParams.districtId;
		if (!streetsOne || districtID !== streetsOne.district.id) {
			return <Redirect404 message404={"!streetsOne || districtID !== streetsOne.district.id"} />;
		}

		if (streetsOne.region.id !== props.ApplicationModel.currentRegion?.id) {
			return (
				<Redirect404
					message404={"streetsOne.region.id !== props.ApplicationModel.currentRegion.id"}
				/>
			);
		}

		let checkDistrict =
			generateValidChankUrl(streetsOne.district.name.toLowerCase()) !== routerParams.districtName;
		let checkStreet = generateStreetPart(streetsOne) !== routerParams.streetName;
		if (checkDistrict || checkStreet) {
			let link = decodeURIComponent(
				generateUrl(Routes.AddressStreet.route, {
					regionUrl: defaultParams.regionUrl,
					districtName: generateValidChankUrl(streetsOne.district.name.toLowerCase()),
					districtId: streetsOne.district.id,
					streetName: generateStreetPart(streetsOne),
					streetId: streetsOne.id,
				})
			);
			let location = {
				pathname: link,
				search: historyParams.search,
				state: {
					isRedirect: true,
				},
			};
			return <Redirect to={location} />;
		}

		let cloneHouses = JSON.parse(JSON.stringify(houses));
		let sortHouses = [];
		if (cloneHouses) {
			sortHouses = cloneHouses.sort(function (a, b) {
				if (parseInt(a.full) > parseInt(b.full)) {
					return 1;
				}
				if (parseInt(a.full) < parseInt(b.full)) {
					return -1;
				}
				if (a.full.localeCompare(b.full) >= 0) {
					return 1;
				} else {
					return -1;
				}
			});
		}

		// Некоторые дома должны переводить на уникальный урл
		let sortHouses_ = [];
		sortHouses.map((house) => {
			sortHouses_.push({
				id: house.id,
				name: house.full,
				houseUrl: Boolean(house.url),
				link: Boolean(house.url)
					? `${generateUrl(Routes.RatesInHouse.route, {
							regionUrl: defaultParams.regionUrl,
							districtName: generateValidChankUrl(house.district_name),
							districtId: house.district_id,
							streetName: generateStreetPart({
								type: house.street_type,
								name: house.street_name,
							}),
							streetId: house.street_id,
							house_url: house.url,
							house_id: house.id,
						})}`
					: `${generateUrl(Routes.DomaNzl.route, {
							regionUrl: defaultParams.regionUrl,
						})}`,
				onClickHandler: () => onClickHouseHandler(house),
			});
		});

		topComponent = (
			<SeoBlockHOC route={props.route} placeText="ListOfHouses">
				<ListOfHouses
					text={text}
					defaultItems={sortHouses_}
					windowSizes={windowSizes}
					showAlphabet={true}
					type={"houses"}
					isBlockLetterView={false}
					listHeader={`${text.SEO_ADDRESS_HOUSE_TITLE} ${generateStreetFullName({
						type: streetsOne.type,
						name: streetsOne.name,
					})}`}
				/>
			</SeoBlockHOC>
		);
	}

	return (
		<React.Fragment>
			{canonical()}

			{isMainSiteCode(siteCode) && <div></div>}

			<SeoBlockHOC route={props.route} placeText="AddressSearch">
				<AddressSearchContainer />
			</SeoBlockHOC>

			<Banner101Rubles
				text={text}
				viewBanner={siteConfig?.settings.banner101rubles}
				bannerLocation={"street"}
				sendingStore={sendingStore}
			/>
			<Container>
				<SeoAddressLayout
					topComponent={topComponent}
					middleComponent={
						<>
							<SeoBlockHOC route={props.route} placeText="ProvidersList">
								<ProvidersContainer
									ApplicationModel={props.ApplicationModel}
									params={{ ...props.match.params, ...defaultParams }}
									streetsOne={streetsOne}
								/>
							</SeoBlockHOC>
						</>
					}
				/>

				<PaddingBottom>
					<SeoBlockHOC route={props.route} placeText="RandomMobileTariffList">
						<RandomMobileTariffListHOC
							tariffsLocation="street"
							textHeader={`${
								text.MOBILE_INTERNET_POSSIBILITY_DISTRIBUTION_ROUTER
							}${text.ONTO}${generateStreetFullName({
								type: streetsOne.type,
								name: streetsOne.name,
							})}`}
						/>
					</SeoBlockHOC>
				</PaddingBottom>

				<SeoBlockHOC route={props.route} placeText="YandexWidgetReviews">
					<ContainerPaddingBottom>
						<YandexWidgetReviews contacts />
					</ContainerPaddingBottom>
				</SeoBlockHOC>

				{windowSizes.xs && (
					<VisibilitySensor onChange={(visible) => wasViewedHandler(visible)}>
						<CheckSpeed text={text} sendingStore={sendingStore} />
					</VisibilitySensor>
				)}

				<SeoBlockHOC route={props.route} placeText="ReviewStreet">
					<BlockReviews
						hideTitle={true}
						reviewLocation={"street"}
						queryType={"streetFull"}
						text={text}
						reviews={
							<ReviewListContainerV3 text={text} reviewLocation={"street"} queryType={"reviews"} />
						}
						control={
							<>
								<ReviewsSend101RublesHOC match={props.match}>
									<AddReviewContainerV2
										reviewLocation={"street"}
										queryType={"providers"}
										selectProvidersPlaceholder={text.filtersProvider}
										textHeader={text.write_review_gift_money}
									/>
								</ReviewsSend101RublesHOC>
								{!windowSizes.xs && (
									<VisibilitySensor onChange={(visible) => wasViewedHandler(visible)}>
										<CheckSpeed text={text} sendingStore={sendingStore} />
									</VisibilitySensor>
								)}
							</>
						}
						datatest={"providers_reviews_street"}
					/>
				</SeoBlockHOC>
			</Container>
		</React.Fragment>
	);
}

export default withRouter(inject("ApplicationModel")(Street));
